/**
 * The flight scene's half of the night searchlights: the beam pool, and the
 * per-frame aiming it is fed from.
 *
 * Split out of `flight-scene.ts` for the reason `scene-particles.ts`,
 * `scene-propellers.ts`, `scene-killcam.ts` and `scene-flyby-zoom.ts` were —
 * the host sits close to aislop's size ceiling and a cohesive addition belongs
 * in a sibling. `flight-scene.ts` keeps one field and one constructor call;
 * `scene-draw.ts` makes the frame-loop call. The *rules* — where a beam points,
 * how fast a mount swings, when a battery has one — are in `searchlights.ts`,
 * which imports no `three`; the *look* is in `three/searchlight-beams.ts`.
 *
 * The host is described structurally rather than by importing `SceneState`,
 * which is private to `flight-scene.ts`.
 */

import * as THREE from "three/webgpu";
import type { TimeOfDay } from "@red-opus/engine";
import {
  MAX_SEARCHLIGHTS,
  collectSearchlights,
  emptySearchlightBeam,
  isNight,
  type SearchlightBeam,
} from "./searchlights.ts";
import { SearchlightBeams } from "../three/searchlight-beams.ts";
import { loadSearchlightMounts, type SearchlightMounts } from "../three/searchlight-mount.ts";
import type { EncounterRunner } from "./sim-runner.ts";

export interface Searchlights {
  /** Add this to the scene once; every beam draws inside it. */
  readonly group: THREE.Group;
  /**
   * Whether this mission's briefed hour is one the lights come on at, settled
   * once at construction — a `MissionSpec`'s `timeOfDay` cannot change
   * mid-sortie. Held here rather than on `flight-scene.ts`'s state so the host
   * carries one field for this feature instead of two.
   */
  readonly night: boolean;
  /**
   * Aims and draws this frame's beams. `enabled` false publishes an empty pool,
   * which is what stops the lights existing rather than merely hiding them —
   * and is the whole of the day case, since no mission flown in daylight ever
   * reaches the collector.
   *
   * `dt` of 0 is meaningful: the kill camera re-draws one instant through a
   * second lens, and a mount must not creep because it was looked at twice.
   */
  update(dt: number, runner: EncounterRunner, enabled: boolean): void;
  /**
   * Draw calls the lights add this frame: one for the beams, plus one for each
   * of the mount's three articulated pieces once its model has arrived — and
   * none at all when nothing is lit.
   */
  drawCallCount(): number;
  /** Beams published by the last `update` — what the tests and the draw count read. */
  readonly liveCount: number;
  dispose(): void;
}

class SearchlightsImpl implements Searchlights {
  readonly group = new THREE.Group();
  readonly night: boolean;
  private readonly beams = new SearchlightBeams(MAX_SEARCHLIGHTS);
  /**
   * Filled and re-filled every frame by `collectSearchlights`, never
   * reallocated — and, unlike the particle sources, genuinely *stateful*
   * between frames: each entry carries its own slewed direction, which is what
   * makes a mount swing rather than snap.
   */
  private readonly aims: SearchlightBeam[] = Array.from({ length: MAX_SEARCHLIGHTS }, emptySearchlightBeam);
  /**
   * The modelled projectors, once their `.glb` has arrived — `undefined` until
   * then, and for good on a machine that has never run `bun run enhanced`. A
   * night without them is AD-017 as it originally shipped: beams, and nothing
   * standing under them.
   */
  private mounts: SearchlightMounts | undefined;
  /** Projectors published by the last `update`, working and wrecked alike — what `drawCallCount` reads. */
  private mountCount = 0;
  private disposed = false;

  constructor(timeOfDay: TimeOfDay | undefined) {
    this.night = isNight(timeOfDay);
    this.group.add(this.beams.mesh);
    // Fetched only for a sortie that can actually light them. A daytime mission
    // never asks for the model, which is a request and a pipeline compile it
    // would have no use for.
    if (this.night) void this.adoptMounts();
  }

  private async adoptMounts(): Promise<void> {
    const mounts = await loadSearchlightMounts(MAX_SEARCHLIGHTS);
    if (mounts === undefined) return;
    // The scene may have been torn down while the fetch was in flight — a
    // sortie abandoned at the loading screen — and adding to a disposed group
    // would leak the meshes it just built.
    if (this.disposed) {
      mounts.dispose();
      return;
    }
    this.mounts = mounts;
    this.group.add(...mounts.meshes);
  }

  update(dt: number, runner: EncounterRunner, enabled: boolean): void {
    this.beams.begin();
    this.mounts?.begin();
    this.mountCount = 0;
    if (enabled) {
      const count = collectSearchlights(runner.encounter(), dt, this.aims);
      // The beam leaves the *lamp*, not the ground: its apex is the emplacement
      // lifted to the trunnion and carried forward to the glass, both read off
      // the model itself. With no model there is no lamp and the apex is the
      // emplacement, exactly as before.
      const lift = this.mounts?.trunnionHeightM ?? 0;
      const reach = this.mounts?.lensForwardM ?? 0;
      for (let index = 0; index < count; index += 1) {
        const beam = this.aims[index];
        if (beam === undefined) break;
        this.mountCount += 1;
        // A shot-out post draws its hardware and nothing else: the projector
        // lies where it fell and the beam is simply not pushed, which is the
        // whole of "you put that light out" (AD-017 §11).
        if (beam.wrecked) {
          this.mounts?.pushWreck(beam.originX, beam.originY, beam.originZ, beam.wreckBearingRad);
          continue;
        }
        this.beams.push(
          beam.originX + beam.dirX * reach,
          beam.originY + lift + beam.dirY * reach,
          beam.originZ + beam.dirZ * reach,
          beam.dirX,
          beam.dirY,
          beam.dirZ,
          beam.intensity,
        );
        this.mounts?.push(beam.originX, beam.originY, beam.originZ, beam.dirX, beam.dirY, beam.dirZ);
      }
    }
    this.beams.end();
    this.mounts?.end();
  }

  drawCallCount(): number {
    // Counted off the *mounts*, not the beams: a battery whose lights have all
    // been shot out still draws three instanced pieces of wreckage and no beam
    // at all, and reading `beams.liveCount` here would report that as free.
    if (this.mountCount === 0) return 0;
    return (this.beams.liveCount > 0 ? 1 : 0) + (this.mounts?.meshes.length ?? 0);
  }

  get liveCount(): number {
    return this.beams.liveCount;
  }

  dispose(): void {
    this.disposed = true;
    this.beams.dispose();
    this.mounts?.dispose();
  }
}

/**
 * Builds the beam pool and adds it to `scene`.
 *
 * **Always built, whatever the hour.** Like `createParticleEffects`, the gate is
 * on what is *fed* to the pool, not on whether it exists: an empty pool costs
 * one hidden `InstancedMesh` and zero draw calls, where building it the first
 * night a mission briefs would mean a pipeline compile at scene load — the
 * hitch `flight-scene.ts`'s `compileAsync` call exists to avoid. It attaches
 * itself rather than handing back a group, because `flight-scene.ts` is at
 * aislop's size ceiling and the line it saves is a line that file does not
 * have.
 */
export function createSearchlights(scene: THREE.Scene, timeOfDay: TimeOfDay | undefined): Searchlights {
  const searchlights = new SearchlightsImpl(timeOfDay);
  scene.add(searchlights.group);
  return searchlights;
}

/** What `flight-scene.ts`'s own state has to offer for the searchlights to be wired up. */
export interface SearchlightHost {
  readonly runner: EncounterRunner;
  readonly searchlights: Searchlights;
}

/**
 * Whether the lights are on this frame — **night, and nothing else**.
 *
 * The single place the question is answered, so the pool and any future reader
 * cannot disagree about it. Deliberately not a setting of its own, and no longer
 * gated on the sky treatment either.
 *
 * It used to read `night && effects.sky !== "measured"`, which was never really
 * about the treatment: it was about `measured` drawing a night sortie as the
 * flat measured *daytime* picture, where a beam would have been a shaft of light
 * in a blue noon sky. With the treatment collapsed to a bi-state
 * (`effects-settings.ts`), both pictures a player can choose draw a night as a
 * night, so both get the beams — and the test that is left is the one the
 * feature always meant, `sun.c` `0xf9f5`'s own `sin(sunAngle) < 0`, settled once
 * at construction from the briefed hour (`Searchlights.night`).
 *
 * The `measured` reference is still reachable as `?treatment=measured`, and it does
 * now light beams under a flat blue sky. That is the debug hatch being honest
 * about being a sky reference rather than a lighting model — it changes what the
 * *sky* is painted, and it was never a claim about what stands on the ground.
 */
export function searchlightsLit(host: SearchlightHost): boolean {
  return host.searchlights.night;
}

/** Aims and draws this frame's beams. One call, so `scene-draw.ts` keeps one line. */
export function updateSearchlights(host: SearchlightHost, dt: number): void {
  host.searchlights.update(dt, host.runner, searchlightsLit(host));
}
